'use client';

import { useMemo } from 'react';
import GfInteractiveMap from '@/components/maps/GfInteractiveMap';
import { hotelLinks, fmtKm } from '@/components/accommodation/HotelCard';
import { t } from '@/i18n/translations';

/**
 * Карта отелей города: центр-маркер — центр города, listed-слой — отели
 * (нумерованные маркеры + список). Координаты отелей — из src/data/geo/hotel-coords.json
 * (`npm run geo:build`). Тонкий адаптер над GfInteractiveMap.
 */
export default function CityHotelsMap({ hotels = [], cityCoords, cityName = '', lang = 'ru', citySlug }) {
    const isRu = lang === 'ru';
    const ui = t(lang, 'accommodationUi');

    const points = useMemo(
        () => (hotels || [])
            .filter((h) => h?.coords?.lat && h?.coords?.lng)
            .map((h) => {
                const { bookingHref, tripHref } = hotelLinks(h, { lang, citySlug });
                const area = isRu ? h.area?.ru : h.area?.en;
                return {
                    id: h._id || h.slug,
                    title: h.name,
                    subtitle: isRu ? h.shortDescriptionRu : h.shortDescriptionEn,
                    coords: h.coords,
                    cover: h.cover,
                    href: bookingHref || tripHref || null,
                    meta: typeof h.distCenterKm === 'number'
                        ? ui.map.kmUnit(fmtKm(h.distCenterKm, isRu))
                        : (area || null),
                };
            }),
        [hotels, lang, citySlug, isRu, ui]
    );

    if (points.length === 0) return null;

    const layers = [{
        key: 'hotels',
        kind: 'poi',
        listed: true,
        swatch: 'var(--pine)',
        label: ui.map.layer,
        points,
    }];

    return (
        <GfInteractiveMap
            layers={layers}
            center={cityCoords?.lat && cityCoords?.lng ? { coords: cityCoords, label: cityName } : null}
            panelEyebrow={ui.map.panelEyebrow}
            hint={ui.map.hint}
            ariaLabel={ui.map.ariaLabel}
            lang={lang}
            zoom={13}
            fitMaxZoom={15}
        />
    );
}
